import { useQuery } from '@apollo/client'
import { useEffect, useRef, useState } from 'react'

import { EVALUATE_PROMOTION_QUERY } from '../../../../graphql/promotions'
import { AppliedPromotion, PromotionEvaluation } from '../../../../types/cart'

/**
 * What the property is already applying to this cart, needing nothing from the operator.
 *
 * Split out of `usePromotionEvaluation` rather than living beside the code check, and the
 * split is a bug fix rather than tidying. The two share a query but nothing else: this one
 * runs on mount with no code and answers "what does the property give this booking"; the
 * other runs on demand and answers "does THIS code work". Sharing one `useLazyQuery` meant
 * sharing its `data`, `loading` and `error`, and all three leaked:
 *
 *   - `loading` from this fetch drove the Apply button's busy state, so arriving at the
 *     cart with a code already in the field showed a spinner for an operation nobody had
 *     started
 *   - `error` from this fetch could never surface, because the caller gated `failed` on a
 *     code having been checked — so a failed lookup rendered as nothing, indistinguishable
 *     from "still loading", forever and without a retry
 *   - `data` from a code check kept feeding the total after the code was removed
 *
 * A plain `useQuery` also re-runs on `cartID` by itself. The cart ID is not enough here,
 * though: quantity and add-on edits retain that ID, so `cartKey` explicitly refetches the
 * evaluation whenever the server cart snapshot changes.
 *
 * `applyAutomatic: true` with no code is a valid intent — loyalty's `resolveIntent` rejects
 * only an intent where NEITHER is set. The query is read-only: it reserves nothing and
 * creates no guest record, so it is safe to run on arrival.
 *
 * `network-only` is deliberate. A cart is mutable server-side state — price rules,
 * availability and eligibility all move — so returning to this step re-asks rather than
 * trusting a figure cached before the operator left it.
 *
 * Refetch results are fenced by a generation as well as `cartKey`. Without both, rapid
 * cart edits can let an older request finish last and put the old total back on screen.
 * While a new snapshot is being evaluated the old result is hidden, so the operator is
 * never shown a discount calculated for different contents.
 */
export function usePropertyDiscounts(
  cartID: string,
  cartKey = cartID
): {
  /** Undefined until the first response. `[]` means none apply, which must be SAID. */
  automatic?: AppliedPromotion[]
  /** The cart total with no code applied. */
  totalDiscountMinor?: string
  currency?: string
  failed: boolean
} {
  const { data, error, refetch } = useQuery<{
    evaluatePromotion: PromotionEvaluation
  }>(EVALUATE_PROMOTION_QUERY, {
    variables: { cartID, intent: { applyAutomatic: true } },
    fetchPolicy: 'network-only'
  })

  const generation = useRef(0)
  const lastCartKey = useRef(cartKey)
  const [snapshot, setSnapshot] = useState<{
    cartKey: string
    evaluation?: PromotionEvaluation
    failed: boolean
  }>()

  useEffect(() => {
    if (lastCartKey.current === cartKey) return

    lastCartKey.current = cartKey
    const requestGeneration = generation.current + 1
    generation.current = requestGeneration
    setSnapshot(undefined)

    void refetch()
      .then((result) => {
        if (generation.current === requestGeneration) {
          setSnapshot({
            cartKey,
            evaluation: result.data.evaluatePromotion,
            failed: false
          })
        }
      })
      .catch(() => {
        if (generation.current === requestGeneration) {
          setSnapshot({ cartKey, failed: true })
        }
      })
  }, [cartKey, refetch])

  useEffect(() => {
    // Only the initial query reads Apollo's shared result slot. Refetches use their own
    // invocation promises above, preventing out-of-order responses from winning.
    if (generation.current !== 0) return

    if (data) {
      setSnapshot({
        cartKey,
        evaluation: data.evaluatePromotion,
        failed: false
      })
    } else if (error) {
      setSnapshot({ cartKey, failed: true })
    }
  }, [cartKey, data, error])

  const current = snapshot?.cartKey === cartKey ? snapshot : undefined
  const evaluation = current?.evaluation

  return {
    automatic: evaluation?.applied.filter(
      (promotion) => promotion.distribution !== 'CodeEntry'
    ),
    totalDiscountMinor: evaluation?.totalDiscountMinor,
    currency: evaluation?.currency,
    failed: current?.failed ?? false
  }
}
